'use client';

import type { DragEndEvent, DragStartEvent, UniqueIdentifier } from '@dnd-kit/core';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { IcDrag, IconButton, Padding, Typography } from '@musinsa/mds';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { twMerge } from 'tailwind-merge';
import { Draggable } from '../../shop-edit/draggable';

interface CurationData {
  curationId: number | string;
  title: string;
}

interface Props {
  className?: string;
  curations: CurationData[];
  onOrderChange: (orderedIds: (number | string)[]) => void;
}

export const VirtualizedDraggableCurationList = ({
  className,
  curations,
  onOrderChange,
}: Props) => {
  const [dragCurations, setDragCurations] = useState<CurationData[]>(curations);
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const virtuosoRef = useRef<VirtuosoHandle>(null);
  const { isTouchDevice } = useDeviceInfo();

  // curations의 길이가 변경되었을 때만 동기화 (아이템 추가/삭제 시)
  // 순서만 변경된 경우에는 동기화하지 않음
  useEffect(() => {
    if (curations.length !== dragCurations.length) {
      setDragCurations(curations);
    }
  }, [curations, dragCurations.length]);

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8, // 8px 이동해야 드래그 시작
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 300, // 250ms 길게 눌러야 드래그 시작
      tolerance: 5, // 5px 이내 움직임은 허용
    },
  });

  const sensors = useSensors(isTouchDevice ? touchSensor : pointerSensor);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(event.active.id);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!over || !dragCurations.length) {
        setActiveId(null);
        return;
      }

      if (active.id !== over.id) {
        const oldIndex = dragCurations.findIndex(
          (item) => item.curationId.toString() === active.id,
        );
        const newIndex = dragCurations.findIndex((item) => item.curationId.toString() === over.id);

        const newItems = arrayMove(dragCurations, oldIndex, newIndex);
        setDragCurations(newItems);

        // 부모 컴포넌트에 ID 배열 전달
        const newOrderedIds = newItems.map((item) => item.curationId);
        onOrderChange(newOrderedIds);
      }

      setActiveId(null);
    },
    [dragCurations, onOrderChange],
  );

  const dragCurationIds = useMemo(
    () => dragCurations.map((item) => item.curationId.toString()),
    [dragCurations],
  );

  // 현재 드래그 중인 아이템 찾기
  const activeItem = activeId
    ? dragCurations.find((curation) => curation.curationId.toString() === activeId.toString())
    : undefined;

  const itemContent = useCallback(
    (index: number) => {
      const curation = dragCurations[index];
      if (!curation) return null;

      return (
        <div>
          <Draggable
            key={curation.curationId}
            id={curation.curationId.toString()}
            className="cursor-grab active:cursor-grabbing"
            isOverlay={false}
          >
            <Padding
              className="flex items-center justify-between hover:bg-gray-50 transition-colors"
              y="3"
              x="4"
            >
              <Typography colorScheme="gray-600" variant="body13pxReg">
                {curation.title}
              </Typography>
              <IconButton size="size16x16" className="cursor-grab">
                <IcDrag />
              </IconButton>
            </Padding>
          </Draggable>
        </div>
      );
    },
    [dragCurations],
  );

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext items={dragCurationIds} strategy={verticalListSortingStrategy}>
        <div className={twMerge(className, 'flex flex-col w-full h-full grow')}>
          <Virtuoso
            ref={virtuosoRef}
            className="w-full h-full grow"
            totalCount={dragCurations.length}
            itemContent={itemContent}
            overscan={10}
          />
        </div>
      </SortableContext>

      <DragOverlay>
        {activeItem && (
          <div className="opacity-90 transform rotate-1 scale-105 shadow-lg">
            <Padding className="flex items-center justify-between bg-gray-50" y="3" x="4">
              <Typography colorScheme="gray-600" variant="body13pxReg">
                {activeItem.title}
              </Typography>
              <IconButton size="size16x16">
                <IcDrag />
              </IconButton>
            </Padding>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};
